import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/apiService';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import Skeleton from '../common/Skeleton';
import { Search, Plus, Save, Check, Trash2, Edit2 } from 'lucide-react';
import { confirmDelete, swalConfirm, swalSuccess } from '../common/confirm';
import StatusSwitch from '../common/StatusSwitch';

interface ComponentRegistryItem {
  id: number; component_code: string; purpose: string; module: string;
  sub_module?: string; type: string; file_path?: string;
  created_by?: string; created_at: string; updated_at: string;
  is_active: boolean; notes?: string;
}

interface ComponentFormData {
  component_code: string; purpose: string; module: string; sub_module: string;
  type: string; file_path: string; is_active: boolean; notes: string;
}

const ComponentRegistryTab: React.FC = () => {
  const [components, setComponents] = useState<ComponentRegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<ComponentRegistryItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<ComponentFormData>({
    component_code: '', purpose: '', module: '', sub_module: '',
    type: '', file_path: '', is_active: true, notes: '',
  });

  const componentTypes = ['listing', 'modal', 'form', 'add', 'edit', 'prompt', 'detail', 'other'];

  const loadComponents = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (moduleFilter) params.append('module', moduleFilter);
      if (typeFilter) params.append('type', typeFilter);
      const response = await apiClient.get(`/api/components?${params}`);
      const result = response.data;
      if (result.success) { setComponents(result.data || []); }
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load components' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, moduleFilter, typeFilter]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  useEffect(() => { setPage(1); }, [searchTerm, moduleFilter, typeFilter, statusFilter, pageSize]);

  const resetForm = () => {
    setFormData({ component_code: '', purpose: '', module: '', sub_module: '', type: '', file_path: '', is_active: true, notes: '' });
    setEditingComponent(null); setIsFormOpen(false);
  };

  const handleCreate = () => { resetForm(); setIsFormOpen(true); };

  const handleEdit = (component: ComponentRegistryItem) => {
    setEditingComponent(component);
    setFormData({ component_code: component.component_code, purpose: component.purpose, module: component.module, sub_module: component.sub_module || '', type: component.type, file_path: component.file_path || '', is_active: component.is_active, notes: component.notes || '' });
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirmDelete({ title: 'Delete this component registry entry?' });
    if (!ok) return;
    try {
      const response = await apiClient.delete(`/api/components/${id}`);
      if (response.data.success) {
        setToast({ type: 'delete', message: 'Component deleted' });
        resetForm();
        await loadComponents();
      }
    } catch { }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = editingComponent ? `/api/components/${editingComponent.id}` : '/api/components';
      const method = editingComponent ? 'put' : 'post';
      const payload = { ...formData, sub_module: formData.sub_module || null, file_path: formData.file_path || null, notes: formData.notes || null };
      const response = await apiClient[method](url, payload);
      if (response.data.success) {
        setToast({ type: 'success', message: editingComponent ? 'Component updated successfully' : 'Component created successfully' });
        resetForm();
        await loadComponents();
      }
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to save component' });
    } finally {
      setSubmitting(false);
    }
  };

  const seedComponents = async () => {
    const ok = await swalConfirm('This will add sample component data.', 'Seed Components');
    if (!ok) return;
    try {
      const response = await apiClient.post('/api/components/seed');
      if (response.data.success) { await loadComponents(); swalSuccess(`Successfully seeded ${response.data.created_count} components`); }
    } catch { }
  };

  const uniqueModules = Array.from(new Set(components.map(c => c.module))).sort();
  const filteredComponents = components.filter(c => {
    const matchesSearch = !searchTerm || c.component_code.toLowerCase().includes(searchTerm.toLowerCase()) || c.purpose.toLowerCase().includes(searchTerm.toLowerCase()) || c.module.toLowerCase().includes(searchTerm.toLowerCase()) || (c.sub_module && c.sub_module.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? c.is_active : !c.is_active);
    return matchesSearch && (!moduleFilter || c.module === moduleFilter) && (!typeFilter || c.type === typeFilter) && matchesStatus;
  });

  const totalRecords = filteredComponents.length;
  const paginatedRows = filteredComponents.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="netbody-card flex flex-col h-full">

      <div className="netbody-header">
        {/* Status — iOS-style on/off switch (on = active) */}
        <StatusSwitch
          value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
          noun="components"
          testId="SEL_component_registry_status"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect value={moduleFilter} onChange={v => setModuleFilter(String(v ?? ''))} className="filter-status" data-testid="SEL_component_registry_module" noSearchIcon options={[{ value: '', label: 'All Modules' }, ...uniqueModules.map(m => ({ value: m, label: m }))]} />
          <FormControl.SearchSelect value={typeFilter} onChange={v => setTypeFilter(String(v ?? ''))} className="filter-status" data-testid="SEL_component_registry_type" noSearchIcon options={[{ value: '', label: 'All Types' }, ...componentTypes.map(t => ({ value: t, label: t }))]} />
          <FormControl.Input
            type="text"
            placeholder="Search components…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_component_registry_search"
          />
          <Button variant="secondary-outline" onClick={seedComponents} data-testid="BTN_component_registry_seed">Seed Sample</Button>
          <Button variant="fill" icon={Plus} onClick={handleCreate} data-testid="BTN_component_registry_create">Create</Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Purpose</th>
                    <th className="table-list-th">Module</th>
                    <th className="table-list-th">Type</th>
                    <th className="table-list-th table-list-th--right">Actions</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill', 'pill', '2xs', { width: 'icon', align: 'right' }]} />}
                  {!loading && filteredComponents.length === 0 && (
                    <tr>
                      <td colSpan={6} className="table-list-empty-cell">No components found. Click "Create" to add one.</td>
                    </tr>
                  )}
                  {!loading && paginatedRows.map((component, idx) => (
                    <tr key={component.id} className={`table-list-row${component.is_active ? '' : ' is-inactive'}`} onClick={() => handleEdit(component)} data-testid={`BTN_component_registry_row_${component.id}_open`}>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell">{component.component_code}</td>
                      <td className="table-list-cell table-list-cell--truncate" title={component.purpose}>
                        <span className="table-list-cell-title">{component.purpose}</span>
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title">{component.module}</span>
                        {component.sub_module && <span className="table-list-cell-sub">{component.sub_module}</span>}
                      </td>
                      <td className="table-list-cell">
                        <span className="status-badge xs info">{component.type}</span>
                      </td>
                      <td className="table-list-cell table-list-cell--actions">
                        <button
                          onClick={e => { e.stopPropagation(); handleEdit(component); }}
                          className="table-list-action table-list-action--edit"
                          title="Edit"
                          data-testid={`BTN_component_registry_row_${component.id}_edit`}
                        >
                          <Edit2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="components"
            />
          </div>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />

      <Modal isOpen={isFormOpen} onClose={resetForm} size="md">
        <Modal.Header>
          {editingComponent ? 'Edit Component' : 'Create Component'}
        </Modal.Header>
        <Modal.Body>
          <form id="component-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_component_registry_form">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <FormControl.Input label="Component Code" required value={formData.component_code} onChange={e => setFormData({ ...formData, component_code: e.target.value })} placeholder="e.g., ORG_APP_LST_001" helper="Unique identifier" data-testid="INP_component_registry_code" />
              <FormControl.SearchSelect
                label="Type"
                required
                options={componentTypes.map(t => ({ value: t, label: t }))}
                value={formData.type || null}
                onChange={v => setFormData({ ...formData, type: String(v ?? '') })}
                placeholder="Select Type"
                data-testid="SEL_component_registry_form_type"
              />
            </div>
            <FormControl.Input label="Purpose" required value={formData.purpose} onChange={e => setFormData({ ...formData, purpose: e.target.value })} placeholder="Brief description of component purpose" data-testid="INP_component_registry_purpose" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <FormControl.Input label="Module" required value={formData.module} onChange={e => setFormData({ ...formData, module: e.target.value })} placeholder="e.g., Organisation, Authority, Users" data-testid="INP_component_registry_module" />
              <FormControl.Input label="Sub-module" value={formData.sub_module} onChange={e => setFormData({ ...formData, sub_module: e.target.value })} placeholder="e.g., IT Applications" data-testid="INP_component_registry_sub_module" />
            </div>
            <FormControl.Input label="File Path" value={formData.file_path} onChange={e => setFormData({ ...formData, file_path: e.target.value })} placeholder="frontend/src/components/..." helper="Can be added later" data-testid="INP_component_registry_file_path" />
            <FormControl.Textarea label="Notes" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional development notes..." rows={3} data-testid="INP_component_registry_notes" />
            <FormControl.Checkbox label="Active" name="is_active" checked={formData.is_active} onChange={checked => setFormData({ ...formData, is_active: checked })} data-testid="CHK_component_registry_active" />
          </form>
        </Modal.Body>
        <Modal.Footer>
          {editingComponent && (
            <div style={{ flex: 1 }}>
              <Button variant="fill" icon={Trash2} type="button" onClick={() => handleDelete(editingComponent.id)} data-testid="BTN_component_registry_delete">Delete</Button>
            </div>
          )}
          <Button type="submit" form="component-form" variant={editingComponent ? 'update' : 'fill'} icon={editingComponent ? Check : Save} disabled={submitting} data-testid="BTN_component_registry_submit">
            {submitting ? (editingComponent ? 'Updating...' : 'Submitting...') : (editingComponent ? 'Update' : 'Submit')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ComponentRegistryTab;
